import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { LegacyPageEvent as PageEvent } from "@angular/material/legacy-paginator";
import { Router } from "@angular/router";
import { Store, select } from "@ngrx/store";
import { MenuService } from "@modules/config/_services/menu/menu.service";
import { SetStatusAction } from "@store/actions/statusNetPayment.action";
import { getGlobalDashboardFilterState, selectStatusNetPayment } from "@store/index";
import { AppState } from "@store/models/state.model";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { Location } from "@angular/common";

import { ModalLauncherService } from "@shared/components/wpay-ui/modal/modal-launcher.service";
import {
	cancelBookingDialog,
	providerMessage
} from "@shared/components/wpay-ui/modal/templates/cancel-booking/model/cancel-booking.interface";
import { PayoutsService } from "@shared/services/payouts/payouts.service";
import { Pagination } from "src/app/shared/models/pagination.interface";
import { Milestone, PayCycle } from "src/app/shared/models/pay-cycle.interface";
import { PayGroup } from "src/app/shared/models/pay-groups";
import { MilestoneReport } from "src/app/shared/models/payouts.types";
import { SelectOption } from "src/app/shared/models/select-option.interface";
import { InactivateToggleDialogComponent } from "../../../_components/payouts/payout-list-item/inactivate-toggle-dialog/inactivate-toggle-dialog.component";
import { TppPaymentsApiService } from "../../../services/tpp-payments-api.service";
import { Booking, TppPayouts, cancelReport } from "../../models/tpp-payment.interface";
import { ToastService } from "@shared/services/toast/toast.service";
import { ServiceProviderService } from "@shared/services/service-provider/service-provider.service";
import { AvailableServiceProvider } from "@shared/models/service-provider-types.interface";
import { CommonService } from "@modules/dashboard/services/common.service";
import { HistoryLogType } from "@shared/components/history-log-side-panel/models/history-log-interface";
import { HistoryLogSidePanelService } from "@shared/components/history-log-side-panel/services/history-log-side-panel.service";
import { ExternalCrumb } from "@shared/models/breadcrumbs.interface";
import { UpdateHistoryStateInfoAction } from "@store/actions/historyStateInfo.action";
import { FilterMilestoneTypes } from "@modules/global-dashboard/models/global-dashboard-interface";

@Component({
	selector: "app-tpp-payments",
	templateUrl: "./tpp-payments.component.html",
	styleUrls: ["./tpp-payments.component.scss"]
})
export class TppPaymentsComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild("filters", { static: false }) filtersComponent!: ElementRef;
	@ViewChild("payoutContainer") payoutContainer!: ElementRef;

	pageSize = 10000; //temp
	pageNumber = 0;
	totalPages = 0;
	totalCount = 0;

	//State variabled
	payGroup!: PayGroup;
	payCycle!: PayCycle;
	payCycleId = "";
	milestone!: Milestone;
	dataInputId!: string;
	payrollCycleId = "";
	payCycleMilestones!: Milestone[];

	status = "";

	milestoneReport!: MilestoneReport;

	pagination!: Pagination;

	bookings!: Booking[];

	reprocessPayoutIdsList: string[] = [];

	bookingHasBeeenProcessed = true;

	statusFilter: string[] = [];

	isCancelledRun = false;

	bookingButtonState = "inactive";

	payouts: TppPayouts[] = [];

	//Top card info
	selectedKPI = "";
	payCycleName = "";
	updating = true;
	title = "Booking date / ";
	subtitle = "";
	milestoneGroup = "NETS";
	flag = "GP";

	//Cancelation variables
	runSelection!: SelectOption[];
	cancelReport: cancelReport[] = [];
	cancelId = "reset";
	runName = "";
	cancelDate!: Date;
	cancellerName = "";
	reason = "";

	setFiltersToFixed = false;
	emitScroll = 0;

	providers: AvailableServiceProvider[] = [];
	providerType = "API";

	bulkTransactionIds: string[] = [];

	showHistoryLogPanel = false;
	HistoryLogType = HistoryLogType;

	/* Breadcrumbs */
	headerCrumb = "";
	externalCrumbs: ExternalCrumb[] = [];

	private destroy$: Subject<void> = new Subject<void>();

	/** IMPORT DECLRATIONS */
	private router = inject(Router);
	private tppPaymentsApi = inject(TppPaymentsApiService);
	private serviceProviderService = inject(ServiceProviderService);
	private dialog = inject(MatDialog);
	private modalLauncherService = inject(ModalLauncherService);
	private tppPaymentsApiService = inject(TppPaymentsApiService);
	private payoutService = inject(PayoutsService);
	private menuService = inject(MenuService);
	private store = inject(Store<AppState>);
	private toast = inject(ToastService);
	private readonly _location = inject(Location);
	private commonService = inject(CommonService);
	private historyLogService = inject(HistoryLogSidePanelService);

	constructor() {
		this.serviceProviderService
			.getProviders()
			.pipe(takeUntil(this.destroy$))
			.subscribe(providers => (this.providers = providers));
	}

	ngOnInit(): void {
		this.menuService.setVisible(false);
		// Prioritize deep-link navigation (has state data) over dashboard navigation (uses Redux store)
		if (history.state?.payCycle && history.state?.payGroup && history.state?.milestone) {
			// Deep-link navigation: navigate with state containing payGroup, payCycle, milestone
			this.setHistory();
		} else if (this.router.url === "/global-dashboard/tpp-payments") {
			// Dashboard navigation: get KPI selection from Redux store
			this.getSelectedKPI();
		} else {
			// Fallback: try to load from state
			this.setHistory();
		}

		this.menuService.setVisible(false); //hide sidenav

		this.historyLogService.transactionHistoryLogSubject.pipe(takeUntil(this.destroy$)).subscribe({
			next: res => {
				if (res.payoutId !== "") {
					this.showHistoryLogPanel = true;
				} else {
					this.showHistoryLogPanel = false;
				}
			}
		});
	}

	ngAfterViewInit(): void {
		this.attachScrollListener();
	}

	attachScrollListener(): void {
		const scrollContainer = this.payoutContainer?.nativeElement;
		if (scrollContainer) {
			scrollContainer.addEventListener("scroll", this.onScroll.bind(this));
		}
	}

	onScroll(event: Event): void {
		const scrollContainer = event.target as HTMLElement;
		this.emitScroll = scrollContainer.scrollTop;
	}

	setHistory(): void {
		if (
			history.state?.payCycle &&
			history.state?.payGroup &&
			history.state?.milestone &&
			this.hasDataInputMilestone(history?.state?.milestones)
		) {
			this.payGroup = history.state.payGroup;
			this.payCycle = history.state.payCycle;
			this.milestone = history.state.milestone;
			this.payCycleMilestones = history.state.milestones;
			this.payCycleId = history.state.payCycle.id;
			this.payCycleName = history.state.payCycle.name;
			this.payrollCycleId = history.state.payCycle.payrollCycleId;

			this.getMilestoneReport(this.milestone?.id ? this.milestone.id : "");
			this.headerCrumb = history.state.payrollCycleId
				? `${history.state.payGroup.customer.name} - ${history.state.payCycle.name} - ${history.state.payrollCycleId}, GROUP ${history.state.milestone?.group}`
				: `${history.state.payGroup.customer.name} - ${history.state.payCycle.name}, GROUP ${history.state.milestone?.group}`;

			this.externalCrumbs = [
				{
					crumb: "Booking Date",
					dropDownData: [
						{
							id: this.payCycleMilestones.find(
								milestone =>
									milestone.type === FilterMilestoneTypes.TPP_PAYMENTORDERS &&
									milestone.group === this.milestone.group
							)?.id!,
							name: "Payment Orders"
						},
						{
							id: this.payCycleMilestones.find(
								milestone =>
									milestone.type === FilterMilestoneTypes.FUNDING &&
									milestone.group === this.milestone.group
							)?.id!,
							name: "Funding Date"
						}
					]
				}
			];

			if (this.payGroup?.data && this.payGroup?.legalEntity?.data && this.milestone?.group) {
				this.setupTopCardInfo();
			}

			this.applyDeeplinkPayoutStatusFilter();

			this.setupSubscriptions();
		} else {
			this._location.historyGo(-1);
			this.toast.showError("Something went wrong while trying to load the page. Please try again later.");
		}
	}

	/**
	 * Route user to other milestone pages
	 * @param id: milestone ID
	 */
	routeToMilestonePage(id?: string): void {
		const selectedMilestone = this.payCycleMilestones.find(milestone => milestone.id === id)!;
		let route = "global-dashboard";

		switch (selectedMilestone.type) {
			case FilterMilestoneTypes.TPP_PAYMENTORDERS:
				this.router.navigate([`/${route}/tpp-data-input`], {
					state: {
						payGroup: this.payGroup,
						payCycleId: this.payCycleId,
						payCycleName: this.payCycleName,
						payCycle: this.payCycle,
						milestones: this.payCycleMilestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId,
						previousUrl: this.router.url
					}
				});

				this.store.dispatch(
					new UpdateHistoryStateInfoAction({
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						payCycleName: this.payCycle.name,
						milestones: this.payCycleMilestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId
					})
				);
				break;

			case FilterMilestoneTypes.FUNDING:
				this.router.navigate([`/${route}/funding-date`], {
					state: {
						payGroup: this.payGroup,
						payCycleId: this.payCycleId,
						payCycleName: this.payCycleName,
						milestones: this.payCycleMilestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId
					}
				});

				this.store.dispatch(
					new UpdateHistoryStateInfoAction({
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						payCycleName: this.payCycle.name,
						milestones: this.payCycleMilestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId
					})
				);
				break;

			default:
				break;
		}
	}

	hasDataInputMilestone(milestones: Milestone[]): boolean {
		const dataInputMilestone = milestones?.find(
			(milestone: Milestone) => milestone.type === FilterMilestoneTypes.TPP_PAYMENTORDERS
		);

		if (dataInputMilestone?.id) {
			this.dataInputId = dataInputMilestone.id;
			return true;
		} else {
			return false;
		}
	}

	setupTopCardInfo() {
		this.title = this.title + this.payGroup.data.name;
		this.subtitle = this.commonService.formatMilestonePagesSubtitles(
			this.payGroup.legalEntity.data.name,
			this.payCycleName,
			this.payrollCycleId,
			this.milestone.group
		);
		this.flag = this.payGroup.legalEntity.data.country;
		this.milestoneGroup = this.milestone.group;
	}

	private applyDeeplinkPayoutStatusFilter(): void {
		if (!Object.prototype.hasOwnProperty.call(history?.state || {}, "payoutStatusFilter")) {
			return;
		}

		this.store.dispatch(
			new SetStatusAction({
				statusPayment: history.state.payoutStatusFilter ?? []
			})
		);
	}

	getSelectedKPI(): void {
		this.store.pipe(takeUntil(this.destroy$), select(getGlobalDashboardFilterState)).subscribe(state => {
			if (state?.globalDashboard?.kpiSelected !== undefined) {
				this.selectedKPI = state.globalDashboard.kpiSelected;
				if (state.globalDashboard.kpiSelected === "Failed Transactions") {
					this.title = "Failed transactions / ";
					this.store.dispatch(
						new SetStatusAction({
							statusPayment: ["FAILED,ATTEMPTS_LIMIT_EXCEEDED", "FAILED_TO_BOOK", "PROCESSING,PENDING"]
						})
					);
				}
				this.setHistory();
			} else {
				this.router.navigate(["/global-dashboard"]);
			}
		});
	}

	setupSubscriptions() {
		this.tppPaymentsApiService.reloadPayoutAfterCancelling.pipe(takeUntil(this.destroy$)).subscribe(res => {
			this.loadBookings(
				this.dataInputId,
				this.payCycleId,
				"",
				this.pageSize,
				this.pageNumber,
				this.statusFilter,
				true
			);
		});

		this.store.pipe(takeUntil(this.destroy$), select(selectStatusNetPayment)).subscribe(({ statusPayment }) => {
			this.statusFilter = statusPayment ? statusPayment : [];
			this.loadBookings(
				this.dataInputId,
				this.payCycleId,
				"",
				this.pageSize,
				this.pageNumber,
				this.statusFilter,
				false
			);
		});
	}

	getMilestoneReport(milestoneId: string): void {
		this.updating = true;
		if (milestoneId) {
			this.tppPaymentsApiService
				.getMilestoneReport(milestoneId)
				.pipe(takeUntil(this.destroy$))
				.subscribe(res => {
					this.milestoneReport = res;
					this.updating = false;
				});
		} else {
			this.milestoneReport = this.tppPaymentsApiService.DEFAULT_MILESTONE_VALUES;
			this.updating = false;
		}
	}

	resetCardInfo(): void {
		this.getMilestoneReport(this.milestone?.id ? this.milestone.id : "");
	}

	loadBookings(
		milestoneId: string,
		payCycleId: string,
		subServiceId: string,
		pageSize: number,
		pageNumber: number,
		statusFilter: string[],
		fecthInfoCardsData: boolean
	) {
		this.pageSize = pageSize;
		this.pageNumber = pageNumber;

		this.tppPaymentsApi
			.getBookings(milestoneId, payCycleId, subServiceId, pageSize, pageNumber, statusFilter)
			.pipe(takeUntil(this.destroy$))
			.subscribe((bookings: Booking[]) => {
				//Only show add booking if it has a payout with trans info

				this.bookings = [];
				this.payouts = [];
				this.popFromReprocessList([]);

				bookings.forEach(booking => {
					booking.payouts.forEach(payout => {
						if (payout.transactions.length > 0 && !this.bookings.includes(booking)) {
							this.bookings.push(booking);

							this.payouts.push(payout);
						}
					});
				});

				this.providerType =
					this.providers.find(provider => {
						const providerId = bookings[0]?.payouts[0]?.transactions[0]?.paymentProvider ?? "";
						return provider.id === providerId;
					})?.type ?? "API";

				if (bookings.length > 0) {
					this.bookingButtonState = this.payoutService.getBookingState(this.payouts, this.providerType);
				} else {
					this.bookingButtonState = "inactive";
				}

				this.getCancelReport(this.payGroup.id, this.dataInputId);

				if (fecthInfoCardsData) {
					this.updating = true;
					setTimeout(() => {
						this.resetCardInfo();
					}, 6000);
				}
			});

		this.pagination = this.tppPaymentsApi.getPaginition();
	}

	getCancelledBookings(cancelledId: string) {
		this.pageSize;
		this.pageNumber;

		this.bookings = [];
		this.payouts = [];

		this.tppPaymentsApi
			.getCancelledBookings(cancelledId, this.pageSize, this.pageNumber)
			.pipe(takeUntil(this.destroy$))
			.subscribe((bookings: Booking[]) => {
				//Only show add booking if it has a payout with trans info

				bookings.forEach(booking => {
					booking.payouts.forEach(payout => {
						if (payout.transactions.length > 0 && !this.bookings.includes(booking)) {
							this.bookings.push(booking);
							this.payouts.push(payout);
						}
					});
				});
			});
	}

	getCancelReport(paygroupId: string, g2nMilestoneId: string) {
		this.runSelection = [
			{
				text: "Current Run",
				value: "reset"
			}
		];
		this.tppPaymentsApiService
			.getCancelReport(paygroupId, g2nMilestoneId, 1000, 0)
			.pipe(take(1))
			.subscribe(cancelledRuns => {
				if (cancelledRuns.items.length) {
					cancelledRuns.items.sort(
						(a, b) => new Date(a.canceledAt).getTime() - new Date(b.canceledAt).getTime()
					);

					this.cancelReport = cancelledRuns.items;

					let i = 0;
					cancelledRuns.items.forEach(cancelledRun => {
						i++;

						this.runSelection.push({
							text: "Cancelled run " + i,
							value: cancelledRun.id
						});
					});
				}
			});
	}

	inactivatePayouts(payoutIds: string[]) {
		this.openDialog(payoutIds);
	}

	pushToReprocessList(payoutIds: string[]) {
		payoutIds.forEach(payoutId => {
			if (!this.reprocessPayoutIdsList.find(id => id === payoutId)) {
				this.reprocessPayoutIdsList.push(payoutId);
				this.bookingButtonState = "reprocess";
			}
		});

		this.getBulkTransactionIds();
	}

	popFromReprocessList(payoutIds: string[]) {
		this.reprocessPayoutIdsList =
			payoutIds.length > 0 ? this.reprocessPayoutIdsList.filter(id => !payoutIds.includes(id)) : [];
		if (this.reprocessPayoutIdsList.length === 0) {
			this.bookingButtonState = this.payoutService.getBookingState(this.payouts, this.providerType);
		}

		this.getBulkTransactionIds();
	}

	/**
	 * Gets transaction ids of tpp payout
	 */
	getBulkTransactionIds(): void {
		this.bulkTransactionIds =
			this.reprocessPayoutIdsList.length > 0
				? ([] as string[]).concat(
						...this.payouts
							.filter(payout => this.reprocessPayoutIdsList.includes(payout.id))
							.map(payoutTx => payoutTx.transactions.map(tx => tx.id))
				  )
				: [];
	}

	/**
	 * Trigger inactivation for payouts
	 * @param payoutIds: payout ids
	 */
	bulkInactivate(payoutIds: string[]): void {
		this.tppPaymentsApi
			.inactivateTppPayout(payoutIds)
			.pipe(take(1))
			.subscribe(res => {
				this.reloadTppData();
			});
	}

	openDialog(payoutIds: string[]): void {
		const dialogRef = this.dialog.open(InactivateToggleDialogComponent, {
			width: "666px",
			height: "441px",
			panelClass: "dialog-container",
			data: {
				type: "payout"
			}
		});

		dialogRef.afterClosed().subscribe((result: boolean) => {
			if (result) {
				this.tppPaymentsApi
					.inactivateTppPayout(payoutIds)
					.pipe(take(1))
					.subscribe(res => {
						this.loadBookings(
							this.dataInputId,
							this.payCycleId,
							"",
							this.pageSize,
							this.pageNumber,
							this.statusFilter,
							true
						);
					});
			}
		});
	}

	filterByStatus(status: string[]) {
		if (!status) {
			this.statusFilter = [];
		} else {
			this.statusFilter = status;
		}

		if (!this.isCancelledRun) {
			this.loadBookings(
				this.dataInputId,
				this.payCycleId,
				"",
				this.pageSize,
				this.pageNumber,
				this.statusFilter,
				false
			);
		}
	}

	processBooking() {
		this.tppPaymentsApi
			.processBooking(this.milestone.payCycleId!, this.milestone.payGroupId!, this.milestone.group!)
			.pipe(takeUntil(this.destroy$))
			.subscribe(res => {
				this.loadBookings(
					this.dataInputId,
					this.payCycleId,
					"",
					this.pageSize,
					this.pageNumber,
					this.statusFilter,
					true
				);
				this.statusFilter = [];
			});
	}

	reprocessBooking() {
		this.tppPaymentsApi
			.reProcessBooking(this.reprocessPayoutIdsList)
			.pipe(takeUntil(this.destroy$))
			.subscribe(res => {
				this.statusFilter = [];
				this.reprocessPayoutIdsList = [];
				this.loadBookings(
					this.dataInputId,
					this.payCycleId,
					"",
					this.pageSize,
					this.pageNumber,
					this.statusFilter,
					true
				);
			});
	}

	completePayments() {
		this.tppPaymentsApi
			.completePayments(this.milestone.payCycleId!, this.milestone.payGroupId!, this.milestone.group!)
			.pipe(takeUntil(this.destroy$))
			.subscribe(res => {
				this.loadBookings(
					this.dataInputId,
					this.payCycleId,
					"",
					this.pageSize,
					this.pageNumber,
					this.statusFilter,
					true
				);
				this.statusFilter = [];
			});
	}

	changeRun(cancelId: string) {
		this.cancelId = cancelId;

		if (cancelId !== "reset") {
			this.getCancelledBookings(cancelId);
			this.setupCancelBanner(cancelId);
			this.isCancelledRun = true;
			this.bookingButtonState = "inactive";
		} else {
			this.loadBookings(
				this.dataInputId,
				this.payCycleId,
				"",
				this.pageSize,
				this.pageNumber,
				this.statusFilter,
				false
			);
			this.isCancelledRun = false;
		}
	}

	setupCancelBanner(cancelId: string) {
		const currentReport = this.cancelReport.find(report => report.id === cancelId)!;

		this.runName = this.runSelection.find(run => run.value === cancelId)!.text;
		this.cancelDate = currentReport.canceledAt;
		this.cancellerName = currentReport.canceledBy;
		this.reason = currentReport.reason;
	}

	cancelBooking() {
		const providerMessages: providerMessage[] = [
			{
				provider: "Either Transfermate, Checkout or Ebury",
				message: "After canceling this batch, it is necessary to contact your Service Provider"
			}
		];

		const cancelBooking: cancelBookingDialog = {
			milestoneId: this.dataInputId,
			providerMessages: providerMessages
		};

		this.modalLauncherService.showCancelBookingModal(cancelBooking);
	}

	paginate(pagination: PageEvent) {
		this.pageSize = pagination.pageSize;
		this.pageNumber = pagination.pageIndex;
		this.loadBookings(
			this.dataInputId,
			this.payCycleId,
			"",
			this.pageSize,
			this.pageNumber,
			this.statusFilter,
			false
		);
	}

	reloadTppData(): void {
		this.statusFilter = [];
		this.loadBookings(
			this.dataInputId,
			this.payCycleId,
			"",
			this.pageSize,
			this.pageNumber,
			this.statusFilter,
			true
		);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
